/**
 * =============================================================================
 * WATCH CARD GRID COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 * 
 * Grid container for WatchCards - "Immediate Attention" section.
 * 
 * DESIGN SYSTEM:
 * - Section header: 18-20px
 * - 8pt spacing grid (16px between cards)
 * - No decorative elements
 */

import { Box, Stack, Text, SimpleGrid } from '@mantine/core';
import { memo } from 'react';
import type { WatchCardGridProps } from '../../types';
import { WatchCard } from '../WatchCard';

// =============================================================================
// DESIGN TOKENS
// =============================================================================
const spacing = {
    xs: 8,
    sm: 16,
    md: 24,
} as const;

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const WatchCardGrid: React.FC<WatchCardGridProps> = memo(({
    tagline,
    title,
    cards,
    columns = 3,
    rows = 2,
    className = '',
}) => {
    // If no cards, don't render anything
    if (!cards || cards.length === 0) {
        return null;
    }
    
    // Limit cards to grid capacity
    const maxCards = columns * rows;
    const displayCards = cards.slice(0, maxCards);
    
    return (
        <Stack className={`h-full ${className}`} style={{ gap: spacing.sm }}>
            {/* Header - 18px title */}
            {(tagline || title) && (
                <Box>
                    {title && (
                        <Text 
                            className="text-slate-900"
                            style={{ 
                                fontSize: 18, 
                                fontWeight: 600,
                                lineHeight: 1.3 
                            }}
                        >
                            {title}
                        </Text>
                    )}
                    {tagline && !title && (
                        <Text 
                            className="text-slate-900"
                            style={{ 
                                fontSize: 18, 
                                fontWeight: 600,
                                lineHeight: 1.3 
                            }}
                        >
                            {tagline}
                        </Text>
                    )}
                </Box>
            )}
            
            {/* Grid */}
            <Box className="flex-1 min-h-0">
                <SimpleGrid
                    cols={{ base: 1, sm: 2, md: columns }}
                    spacing={spacing.sm}
                    className="h-full"
                >
                    {displayCards.map((card) => (
                        <WatchCard
                            key={card.id}
                            config={card}
                            className="h-full"
                        />
                    ))}
                </SimpleGrid>
            </Box>
        </Stack>
    );
});

WatchCardGrid.displayName = 'WatchCardGrid';

export default WatchCardGrid;
